<template>
  <div class="formula-wrapper">
    <div class="row no-gutters justify-content-between align-items-center formula-header">
      <div v-if="!imageMaking"
           class="col-xl-auto col-12 d-flex gap-2 order-xl-0 order-0 justify-content-center align-items-center">
        <slot name="left-header"/>
        <a v-for="v in views"
           :key="`view-toggler-${v.id}`"
           class="teeth-toggler pointer"
           :class="{ active: v.id === view.id }"
           @click="view = v">
          {{ $t(v.title) }}
        </a>
      </div>
      <div class="col-xl-auto d-none d-xl-block order-xl-1 order-2"
           v-if="showInstruction">
        <a id="how-to-use-instruction" class="cursor-help text-muted">{{ $t('how_to_use') }}</a>

        <CustomPopover target="how-to-use-instruction"
                       ref="popover"
                       triggers="hover"
                       :arrow="false"
                       custom-class="record-popover">
          <div>
            <div v-html="$t('teeth_selection_instructions')"/>
            <hr/>
            <div v-html="$t('teeth_vertical_selection_instructions')"/>
          </div>
        </CustomPopover>

      </div>
      <div class="col-xl-auto order-xl-2 order-1 col-12 mt-xl-0 mt-2">
        <div v-if="mode === 'select'"
             class="text-center">
          <div class="d-inline-block align-middle"
               :class="{'ml-3': zind > 0}"
               v-for="(z, zind) in additionalSelectZones"
               :key="`zone-${z.id}`">
            <div class="checkbox sm form-group mb-0 position-relative">
              <input type="checkbox"
                     :disabled="disabled"
                     :checked="recordTeeth.includes(z.id)"
                     :id="`zone-${z.id}`"
                     :value="z.id"/>
              <label @click.prevent="selectZone(z.id)"
                     class="mb-0"
                     :for="`zone-${z.id}`">
                {{ $t(z.title) }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-4 text-center" ref="pf">
      <component :is="view.comp"
                 v-if="view"
                 :disabled="disabled"
                 :imageMaking="imageMaking"
                 id="patientFormulaSelectedView"
                 :mode="mode"/>
    </div>
    <ToothStatusModal v-if="statuses && !imageMaking"/>
  </div>
</template>

<script setup>
import AdultTeeth from "@/components/parts/patients/teeth/AdultTeeth"
import BabyTeeth from "@/components/parts/patients/teeth/BabyTeeth"
import { useStore} from "vuex"
import ToothStatusModal from "@/components/parts/patients/teeth/ToothStatusModal"
import {computed, getCurrentInstance, onBeforeMount, onMounted, onUnmounted, ref, watch} from "vue"
import CustomPopover from "@/components/parts/calendar/CustomPopover.vue"

const emit = defineEmits(["input", "loaded"])

const props = defineProps({
  mode: {
    type: String,
    default: 'status'
  },
  showInstruction: {
    type: Boolean,
    default: false
  },
  imageMaking: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const store = useStore()
let bvModal = null

const view = ref(null)
const views = [
  { id: 'AdultTeeth', title: 'permanent_teeth_short', comp: AdultTeeth },
  { id: 'BabyTeeth', title: 'baby_teeth_short', comp: BabyTeeth },
]
const additionalSelectZones = [
  { id: "В", title: "top_jaw" },
  { id: "Н", title: "bottom_jaw" },
  { id: "Р", title: "oral_cavity" },
]

const patient = computed(() => store.state.patient.patient)
const selectedTooth = computed(() => store.state.teeth.selectedTooth)
const selectedToothSelectionType = computed(() => store.state.teeth.selectedToothSelectionType)
const recordTeeth = computed(() => store.getters.recordTeeth)
const statuses = computed(() => store.state.teeth.toothStatuses)
const passedPatientFormulaView = computed(() => store.state.teeth.passedPatientFormulaView)

const selectZone = (zone) => {
  if(props.disabled) return
  store.commit('setSelectedTooth', {
    tooth: zone,
    area: 'tooth',
    type: 'select'
  })
}

onBeforeMount(() => {
  const instance = (getCurrentInstance())
  bvModal = instance?.ctx?._bv__modal
  view.value = views[0]
})

const handleKeyDown = (e) => {
  if (e.key === 'Shift') {
    store.state.teeth.isShiftPressed = true
  }
}

const handleKeyUp = (e) => {
  if (e.key === 'Shift') {
    store.state.teeth.isShiftPressed = false
    store.state.teeth.sameGroupSelectedTooth = false
  }
}

onMounted(async () => {
  window.addEventListener('keydown', handleKeyDown)
  window.addEventListener('keyup', handleKeyUp)
  if(patient.value) {
    await store.dispatch('getPatientTeeth', patient.value?.id)
    // store.commit('setEloquentOptions', null) // don't remember why it's for, but it interrupts other datatables
  }
  emit('loaded')
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
  window.removeEventListener('keyup', handleKeyUp)
})

watch(selectedTooth, (val) => {
  if(val) {
    if(['status','select'].includes(props.mode) && val && selectedToothSelectionType.value !== 'select' ) {
      bvModal.show('tooth-status-modal')
    }
    if(['select'].includes(props.mode) && val && selectedToothSelectionType.value === 'select' ) {
      emit('input', val)
      store.commit("setSelectedTooth", { tooth: null, area: null })
    }
  }
})

watch(passedPatientFormulaView, (val) => {
  if(val) {
    const viewSelected = views.find(v => v.id === val)
    if(viewSelected) {
      view.value = viewSelected
      store.commit('setPassedPatientFormulaView', null)
    }
  }
})
</script>

<style scoped lang="scss">
.formula-header {
  color: $label-color;
}
.formula-wrapper {
  //padding: 22px;
  max-width: 1100px;
  overflow-x: auto;
  margin-left: auto;
  margin-right: auto;
  white-space: nowrap;
  padding: 0 0 30px;
  .teeth-toggler {
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $gray-text;
    height: 24px;
    padding: 0 8px;
    &.active, &:hover {
      text-decoration: none;
      background: $body-bg;
      color: $blue-color;
    }
  }
}

@media screen and (max-width: 768px) {
  .formula-wrapper {
    //padding: 20px 12px;
    //overflow: scroll;
    //white-space: nowrap;
    .teeth-toggler {
      border-radius: 15px;
      margin: 0 4px;
      font-size: 12px;
      padding: 7px 10px;
    }
  }
}

</style>
